import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useMatomo } from '@mitresthen/matomo-tracker-react';
import './App.css';
import ScreenD from './screens/ScreenD';
import ScreenE from './screens/ScreenE';
import ScreenF from './screens/ScreenF';
import ScreenG from './screens/ScreenG';
import ScreenH from './screens/ScreenH';
import ScreenI from './screens/ScreenI';
import ScreenJ from './screens/ScreenJ';
import ScreenK from './screens/ScreenK';

import ScreenM from './screens/ScreenM';
import ScreenN from './screens/ScreenN';
import ScreenO from './screens/ScreenO';
import ScreenP from './screens/ScreenP';
import ScreenT from './screens/ScreenT';
import ScreenU from './screens/ScreenU';
import ScreenV from './screens/ScreenV';
import ScreenW from './screens/ScreenW';
import ScreenX from './screens/ScreenX';
import ScreenTest from './screens/ScreenTest';
import { AppStateProvider, useAppState } from './state/AppState';
import { useQueryParamsInitialization } from './hooks/useQueryParamsInitialization';
import SideMenu from './components/SideMenu';
import TopNav from './components/TopNav';
import Footer from './components/Footer';
import { useGlobalSessionSave } from './hooks/useGlobalSessionSave';
import { useStrictLoadingOrder } from './hooks/useStrictLoadingOrder';
import { getSessionTimeoutSeconds, getSessionExtensionTimeSeconds } from './config/sessionConfig';
import TimerWarningModal from './components/TimerWarningModal';
import ErrorModal, {
  GENERIC_ERROR_MODAL_MESSAGE,
  GENERIC_ERROR_MODAL_TITLE,
} from './components/ErrorModal';
import { setTimerStart } from './services/sessionService';
import { getConfig } from './services/configService';

/**
 * App - The root component for the Mindful Therapy Group frontend.
 *
 * Responsibilities:
 *   - Provides global state and query param initialization.
 *   - Renders the global navigation (TopNav) and SideMenu.
 *   - Handles all routing for the application.
 */

// Component to handle query parameters initialization (UTM, provider, etc.)
function QueryParamsInitializer() {
  useQueryParamsInitialization();
  return null;
}

// Component to scroll to top when route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Component to track page views with Matomo
function PageTracker() {
  const location = useLocation();
  const { trackPageView } = useMatomo();
  const trackingInitialized = React.useRef(false);

  useEffect(() => {
    // Initialize Matomo tracking features once on first load
    if (!trackingInitialized.current && typeof window !== 'undefined' && (window as any)._paq) {
      // Enable additional tracking features to match standard Matomo setup
      (window as any)._paq.push(['trackAllContentImpressions']);
      (window as any)._paq.push(['enableLinkTracking']);
      (window as any)._paq.push(['alwaysUseSendBeacon']);
      // Enable heatmaps and session recording
      (window as any)._paq.push(['HeatmapSessionRecording::enable']);
      trackingInitialized.current = true;
    }
    
    trackPageView({
      documentTitle: document.title,
      href: window.location.href,
    });
  }, [location, trackPageView]);

  return null;
}


// Component to handle global session saving
function GlobalSessionSaver() {
  useGlobalSessionSave();
  return null;
}

// Component to handle timer warning modal
function TimerWarningModalHandler() {
  const { state, dispatch } = useAppState();
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    if (!state.reservationStartTime) return;

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = now - (state.reservationStartTime || 0);
      const elapsedSeconds = Math.floor(elapsed / 1000);
      const totalDuration = getSessionTimeoutSeconds(); // Configurable session timeout
      const actualRemaining = Math.max(totalDuration - elapsedSeconds, 0);
      
      setTimeRemaining(actualRemaining);

      // Hide modal if timer expires
      if (actualRemaining === 0 && !state.timerWarningModalDismissed) {
        dispatch({ type: 'DISMISS_TIMER_WARNING_MODAL' });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [state.reservationStartTime, state.timerWarningModalDismissed, dispatch]);

  const handleDismiss = async () => {
    try {
      const config = getConfig();
      const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';
      
      // Set new timer start time to extend the session
      await setTimerStart(baseUrl, new Date().toISOString());
      
      // Update the app state with new timer start time
      dispatch({ type: 'SET_RESERVATION_START_TIME', payload: Date.now() });
      
      // Reset the modal dismissed flag so it can show again for the next warning period
      dispatch({ type: 'RESET_TIMER_WARNING_MODAL' });
    } catch (error) {
      // Still dismiss the modal even if the API call fails
      dispatch({ type: 'DISMISS_TIMER_WARNING_MODAL' });
    }
  };

  // Show modal when we have extension time remaining
  const extensionTime = getSessionExtensionTimeSeconds();
  const shouldShowModal = Boolean(
    state.reservationStartTime && 
    timeRemaining > 0 && 
    timeRemaining <= extensionTime && // Configurable extension time
    !state.timerWarningModalDismissed
  );

  return (
    <TimerWarningModal
      isVisible={shouldShowModal}
      timeRemaining={timeRemaining}
      onDismiss={handleDismiss}
    />
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuOpen = () => setMenuOpen(true);
  const handleMenuClose = () => setMenuOpen(false);

  return (
    <AppStateProvider>
      <AppContent 
        menuOpen={menuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
      />
    </AppStateProvider>
  );
}

function AppContent({ menuOpen, onMenuOpen, onMenuClose }: {
  menuOpen: boolean;
  onMenuOpen: () => void;
  onMenuClose: () => void;
}) {
  const loadingState = useStrictLoadingOrder();

  return (
    <>
      <QueryParamsInitializer />
      <TimerWarningModalHandler />
      <ScrollToTop />
      <PageTracker />
      {/* Skip link for keyboard users */}
      <a 
        href="#main-content" 
        style={{
          position: 'absolute',
          top: '-40px',
          left: '6px',
          background: '#185F93',
          color: 'white',
          padding: '8px',
          textDecoration: 'none',
          borderRadius: '4px',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '600',
        }}
        onFocus={(e) => {
          e.target.style.top = '6px';
        }}
        onBlur={(e) => {
          e.target.style.top = '-40px';
        }}
      >
        Skip to main content
      </a>
      <TopNav onMenu={onMenuOpen} />
      <SideMenu open={menuOpen} onClose={onMenuClose} />
      <GlobalSessionSaver />
      {loadingState.show400Error && (
        <ErrorModal
          isOpen={loadingState.show400Error}
          onClose={loadingState.handleCloseError}
          onGoBack={loadingState.handleGoBack}
          title={loadingState.errorTitle || GENERIC_ERROR_MODAL_TITLE}
          message={loadingState.errorMessage || GENERIC_ERROR_MODAL_MESSAGE}
        />
      )}
      <Routes>
        <Route path="/" element={<Navigate to={`/d${window.location.search}`} replace />} />
        <Route path="/d" element={<ScreenD />} />
        <Route path="/f" element={<ScreenF />} />
        <Route path="/g" element={<ScreenG />} />
        <Route path="/h" element={<ScreenH />} />
        <Route path="/i" element={<ScreenI />} />
        <Route path="/j" element={<ScreenJ />} />
        <Route path="/k" element={<ScreenK />} />
        <Route path="/e" element={<ScreenE />} />
        <Route path="/m" element={<ScreenM />} />
        <Route path="/n" element={<ScreenN />} />
        <Route path="/o" element={<ScreenO />} />
        <Route path="/p" element={<ScreenP />} />
        <Route path="/t" element={<ScreenT />} />
        <Route path="/u" element={<ScreenU />} />
        <Route path="/v" element={<ScreenV />} />
        <Route path="/w" element={<ScreenW />} />
        <Route path="/x" element={<ScreenX />} />
        <Route path="/test" element={<ScreenTest />} />
        <Route path="*" element={<Navigate to="/d" replace />} />
      </Routes>
      <Footer />
    </>
  );
}

export default App;
